import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, DollarSign, Loader2, Camera, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

// Iter 2026-02-payments-dashboard: canonical payment types shown to users.
// Kept in sync with backend CANONICAL_PAYMENT_METHODS (invoice_payments.py).
const METHODS = [
  { v: "credit_card", l: "Credit card" },
  { v: "cash", l: "Cash" },
  { v: "check", l: "Check" },
  { v: "venmo", l: "Venmo" },
  { v: "cash_app", l: "Cash App" },
  { v: "apple_pay", l: "Apple Pay" },
  { v: "other", l: "Other" },
];

export const PAYMENT_METHOD_OPTIONS = METHODS;
export const PAYMENT_METHOD_LABELS = METHODS.reduce((a, m) => { a[m.v] = m.l; return a; }, {});

/**
 * RecordPaymentDialog — reusable payment recording UI.
 * Prefills amount to remaining balance so a single click marks a full payment.
 */
export default function RecordPaymentDialog({ invoice, open, onOpenChange, onSaved }) {
  const balance = Number(invoice?.balance ?? 0);
  const [amount, setAmount] = useState(String(balance || ""));
  const [method, setMethod] = useState("credit_card");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Iter 2026-02: Check-specific fields — only shown/sent when method === "check"
  const [checkNumber, setCheckNumber] = useState("");
  const [checkPhoto, setCheckPhoto] = useState(null);         // File object
  const [checkPhotoPreview, setCheckPhotoPreview] = useState(null); // objectURL

  // Reset form when invoice changes / dialog reopens
  const resetFor = (inv) => {
    setAmount(String(Number(inv?.balance ?? 0)));
    setMethod("credit_card");
    setPaidAt(new Date().toISOString().slice(0, 10));
    setNotes("");
    setCheckNumber("");
    if (checkPhotoPreview) URL.revokeObjectURL(checkPhotoPreview);
    setCheckPhoto(null);
    setCheckPhotoPreview(null);
  };

  useEffect(() => () => { if (checkPhotoPreview) URL.revokeObjectURL(checkPhotoPreview); }, [checkPhotoPreview]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10 MB"); return; }
    if (checkPhotoPreview) URL.revokeObjectURL(checkPhotoPreview);
    setCheckPhoto(f);
    setCheckPhotoPreview(URL.createObjectURL(f));
  };
  const clearPhoto = () => {
    if (checkPhotoPreview) URL.revokeObjectURL(checkPhotoPreview);
    setCheckPhoto(null); setCheckPhotoPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > balance + 0.01) { toast.error(`Amount exceeds remaining balance of $${balance.toFixed(2)}`); return; }
    if (method === "check" && !checkNumber.trim()) {
      toast.error("Please enter the check number");
      return;
    }
    setSaving(true);
    try {
      let checkPhotoId = null;
      // Upload the check photo first (as an invoice-scoped attachment) so
      // the transaction row can reference it. Fail-open: if upload fails,
      // we still let the admin record the payment — they'll get a toast.
      if (method === "check" && checkPhoto && invoice?.id) {
        try {
          const fd = new FormData();
          fd.append("file", checkPhoto);
          fd.append("resource_type", "invoice");
          fd.append("resource_id", invoice.id);
          const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          checkPhotoId = up.id;
        } catch (err) {
          toast.error("Check photo failed to upload — payment will still be recorded.");
        }
      }
      const payload = {
        amount: amt, method, paid_at: paidAt, notes,
        check_number: method === "check" ? checkNumber.trim() : undefined,
        check_photo_file_id: checkPhotoId || undefined,
      };
      const { data } = await api.post(`/invoices/${invoice.id}/payments`, payload);
      const isFull = data.status === "paid";
      toast.success(isFull ? `Marked as Paid · $${amt.toFixed(2)}` : `Partial payment recorded · $${amt.toFixed(2)}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) resetFor(invoice); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600"/> Record payment</DialogTitle>
          <DialogDescription>
            Invoice {invoice?.invoice_number} — remaining balance <span className="font-semibold text-zinc-900">${balance.toFixed(2)}</span>. If you enter the full remaining amount, it will be marked as Paid automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                <Input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-6 font-mono-accent" data-testid="payment-amount-input" autoFocus/>
              </div>
              {parseFloat(amount) > 0 && parseFloat(amount) < balance - 0.01 && (
                <div className="text-[10px] text-amber-600 mt-1 font-mono-accent uppercase tracking-widest">Partial · ${(balance - parseFloat(amount)).toFixed(2)} still due</div>
              )}
              {parseFloat(amount) >= balance - 0.005 && parseFloat(amount) <= balance + 0.01 && (
                <div className="text-[10px] text-emerald-600 mt-1 font-mono-accent uppercase tracking-widest inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Full balance · will mark Paid</div>
              )}
              {parseFloat(amount) > balance + 0.01 && (
                <div className="text-[10px] text-rose-600 mt-1 font-mono-accent uppercase tracking-widest">Exceeds balance by ${(parseFloat(amount) - balance).toFixed(2)}</div>
              )}
            </div>
            <div>
              <Label>Payment date</Label>
              <Input type="date" required value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="payment-date-input"/>
            </div>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Iter 2026-02 — check-specific fields */}
          {method === "check" && (
            <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="payment-check-block">
              <div>
                <Label>Check number</Label>
                <Input
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  placeholder="e.g. 1042"
                  required
                  data-testid="payment-check-number-input"
                  className="font-mono-accent"
                />
              </div>
              <div>
                <Label>Photo of check <span className="text-zinc-400 font-normal text-xs">(optional)</span></Label>
                {checkPhotoPreview ? (
                  <div className="relative inline-block mt-1" data-testid="payment-check-photo-preview">
                    <img src={checkPhotoPreview} alt="Check" className="h-24 rounded border border-zinc-200 object-cover"/>
                    <button type="button" onClick={clearPhoto} className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-200 rounded-full p-0.5 shadow" title="Remove photo" data-testid="payment-check-photo-clear-btn">
                      <X className="w-3 h-3 text-zinc-600"/>
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-[#FF5A00]" data-testid="payment-check-photo-camera-btn">
                      <Camera className="w-4 h-4"/> Take photo
                      <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden"/>
                    </label>
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-[#FF5A00]" data-testid="payment-check-photo-upload-btn">
                      <ImageIcon className="w-4 h-4"/> Upload
                      <input type="file" accept="image/*" onChange={onPhoto} className="hidden"/>
                    </label>
                  </div>
                )}
                <div className="text-[10px] text-zinc-500 mt-1 font-mono-accent uppercase tracking-widest">Kept in records for reconciliation</div>
              </div>
            </div>
          )}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Check #1042" data-testid="payment-notes-input"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Recording…</> : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----- Status pill helper (shared) -----
export const INVOICE_STATUS_META = {
  draft:     { label: "Draft",         cls: "bg-zinc-100 text-zinc-700 border-zinc-200",        dot: "bg-zinc-400" },
  sent:      { label: "Sent",          cls: "bg-sky-100 text-sky-700 border-sky-200",           dot: "bg-sky-500" },
  viewed:    { label: "Viewed",        cls: "bg-indigo-100 text-indigo-700 border-indigo-200",  dot: "bg-indigo-500" },
  partial:   { label: "Partial",       cls: "bg-amber-100 text-amber-700 border-amber-200",     dot: "bg-amber-500" },
  paid:      { label: "Paid",          cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  overdue:   { label: "Overdue",       cls: "bg-rose-100 text-rose-700 border-rose-200",        dot: "bg-rose-500" },
  void:      { label: "Void",          cls: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through", dot: "bg-zinc-400" },
  cancelled: { label: "Cancelled",     cls: "bg-zinc-100 text-zinc-500 border-zinc-200",        dot: "bg-zinc-400" },
};

export function InvoiceStatusPill({ status, computed, size = "md" }) {
  const effective = computed || status;
  const meta = INVOICE_STATUS_META[effective] || INVOICE_STATUS_META.draft;
  const sz = size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1";
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-md capitalize font-medium ${meta.cls} ${sz}`} data-testid={`invoice-status-${effective}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}/>
      {meta.label}
    </span>
  );
}
